<template>
  <b-modal :id="modalId" :title="isEditing ? 'Edit Lesson' : 'Add New Lesson'" size="lg" centered @hidden="resetModal">
    <b-form @submit.prevent="handleSubmit">
      <!-- Title -->
      <b-form-group label="Title" label-for="lesson-title" label-class="form-label">
        <b-form-input id="lesson-title" v-model="formData.title" placeholder="Enter lesson title" required />
      </b-form-group>

      <!-- Description -->
      <b-form-group label="Description" label-for="lesson-description" label-class="form-label">
        <div class="editor-container">
          <editor
            :key="'editor-' + editorKey"
            :api-key="tinemceApiKey"
            :init="{
              height: 400,
              menubar: false,
              plugins: [
                'advlist autolink lists link',
                'searchreplace visualblocks',
                'insertdatetime media table paste',
                'help wordcount code preview',
              ],
              toolbar:
                'undo redo | formatselect | bold italic backcolor | ' +
                'alignleft aligncenter alignjustify | ' +
                'bullist numlist outdent indent | removeformat | help',
              statusbar: false,
              branding: false,
            }"
            v-model="formData.description"
            placeholder="Enter lesson description"
          />
        </div>
      </b-form-group>

      <!-- Video URL -->
      <div class="row mb-4">
        <span class="form-label col-md-6">Lesson Video</span>
        <div class="col-md-6" style="display: flex; justify-content: end;">
          <b-btn
            v-if="!formData.content_video"
            variant="primary"
            :class="{ 'mb-2': formData.content_video }"
            style="width: 150px"
            @click.prevent="$refs.uploader.click()"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <i v-if="isUploadingVideoUrl" class="fas fa-circle-notch fa-spin"></i>
            <span v-else>Upload Video</span>
          </b-btn>
          <b-btn
            v-else
            variant="danger"
            :class="{ 'mb-2': formData.content_video }"
            style="width: 150px"
            @click.prevent="removeVideo"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <span>Remove Video</span>
          </b-btn>
          <input type="file" ref="uploader" class="d-none" @change="handleFileChange" accept="video/*" />
        </div>
        <div v-if="formData.content_video" class="d-flex align-items-center col">
          <video
            style="width: 100%; margin: auto;"
            :style="{ height: isMobileScreen ? '230px' : '450px' }"
            id="video-preview"
            controls
            :src="formData.content_video"
          />
        </div>
      </div>

      <!-- Order -->
      <!-- <b-form-group label="Order" label-for="lesson-order" label-class="form-label">
        <b-form-input id="lesson-order" v-model.number="formData.order" type="number" min="1" required />
      </b-form-group> -->
    </b-form>
    <b-form-group label-for="quiz" label-class="form-label">
      <label class="form-label"
        >Quiz
        <i
          class="material-icons icon-16pt clickable-item text-muted"
          v-b-popover.hover.top="'This quiz will be taken at the end of the Lesson.'"
        >
          info
        </i></label
      >
      <v-select
        required
        id="quiz"
        class="form-control v-select-custom"
        label="title"
        v-model="formData.quiz"
        :reduce="item => item.id"
        placeholder="Select Quiz"
        :options="quizzes"
        :loading="areQuizzesLoading"
      >
      </v-select>
    </b-form-group>

    <b-form-group v-if="assignments.length > 0" label-for="assignment" label-class="form-label">
      <label class="form-label"
        >Assignment
        <i
          class="material-icons icon-16pt clickable-item text-muted"
          v-b-popover.hover.top="'This quiz will be taken at the end of the Lesson.'"
        >
          info
        </i></label
      >
      <v-select
        required
        id="assignment"
        class="form-control v-select-custom"
        label="title"
        v-model="formData.assignment"
        :reduce="item => item.id"
        placeholder="Select Assignment"
        :options="assignments"
        :loading="areAssignmentsLoading"
      >
      </v-select>
    </b-form-group>
    <template #modal-footer>
      <div class="w-100">
        <b-button variant="outline-secondary" class="float-right" @click="$bvModal.hide(modalId)">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="float-right mr-2"
          @click="handleSubmit"
          :disabled="isSubmitting || isUploadingVideoUrl"
        >
          <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
          {{ isEditing ? 'Save' : 'Add' }} Lesson
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import { mapActions } from 'vuex';

export default {
  name: 'LessonModal',
  components: {
    Editor,
  },
  props: {
    modalId: {
      type: String,
      required: true,
      default: 'lesson-modal',
    },
    quizzes: {
      type: Array,
      default: () => [],
    },
    areQuizzesLoading: {
      type: Boolean,
      default: false,
    },

    lesson: {
      type: Object,
      default: () => ({
        title: '',
        description: '',
        content_video: '',
        order: 1,
        quiz: null,
        assignment: null,
      }),
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      editorKey: 0,
      formData: {
        title: '',
        description: '',
        content_video: '',
        order: 1,
        quiz: null,
        assignment: null,
      },
      isSubmitting: false,
      isUploadingVideoUrl: false,
      windowWidth: window.innerWidth,
      isMounted: false,
      areAssignmentsLoading: false,
      assignments: [],
    };
  },
  watch: {
    lesson: {
      handler(newVal) {
        if (newVal) {
          // Create a new object to trigger reactivity
          this.formData = { ...newVal };
          // Force re-render editor when lesson changes
          if (this.isMounted) {
            this.$nextTick(() => {
              this.editorKey += 1;
            });
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
  },
  mounted() {
    this.fetchAssignments();
    this.isMounted = true;
    // Ensure TinyMCE is properly initialized after the modal is shown
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === this.modalId) {
        // Reset form data when modal is shown
        if (this.lesson) {
          this.formData = { ...this.lesson };
        }
        this.$nextTick(() => {
          this.editorKey += 1;
        });
      }
    });
  },

  beforeDestroy() {
    this.$root.$off('bv::modal::show');
    this.isMounted = false;
  },

  methods: {
    ...mapActions('lessons', ['getLessonImageUploadPresignedUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('assignments', ['getAssignmentsList']),

    resetModal() {
      this.formData = {
        title: '',
        description: '',
        content_video: '',
        order: 1,
        quiz: null,
      };
      // Reset the editor
      this.$nextTick(() => {
        this.editorKey += 1;
      });
      this.$emit('reset');
    },
    async handleSubmit() {
      this.isSubmitting = true;
      try {
        const lessonData = { ...this.formData };
        this.$emit('submit', lessonData);
        this.$bvModal.hide(this.modalId);
      } catch (error) {
        console.error('Error saving lesson:', error);
        this.makeToast({ variant: 'danger', msg: 'Failed to save lesson. Please try again.' });
      } finally {
        this.isSubmitting = false;
      }
    },
    removeVideo() {
      this.formData.content_video = '';
    },
    async fetchAssignments() {
      this.areAssignmentsLoading = true;
      const resp = await this.getAssignmentsList();
      this.assignments = resp.data;
      this.areAssignmentsLoading = false;
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadVideo(file);
      }
    },
    async uploadVideo(file) {
      this.isUploadingVideoUrl = true;

      if (file.size > 25000000) {
        this.makeToast({ variant: 'danger', msg: 'File size should not exceed 25MB' });
        this.isUploadingVideoUrl = false;
        return;
      }
      if (!['video/mp4', 'video/webm'].includes(file.type)) {
        this.makeToast({ variant: 'danger', msg: 'File type should be mp4 or webm' });

        this.isUploadingVideoUrl = false;
        return;
      }

      try {
        const urlResp = await this.getLessonImageUploadPresignedUrl({
          file_name: file.name,
          content_type: file.type,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: file,
        });
        this.formData.content_video = urlResp.data.upload_url.split('?')[0];
      } catch (error) {
        this.isUploadingVideoUrl = false;
        return;
      }
      this.isUploadingVideoUrl = false;
    },
  },
};
</script>

<style scoped>
.tox-tinymce {
  border-radius: 0.25rem;
  border: 1px solid #dae1e7;
}
</style>
